import React, { useRef, useState, useEffect } from 'react';
import { Space, Button, message, Tag, Typography, Popconfirm, Modal, TreeSelect, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { teamApi } from '../../services/team/team';
import type { TeamEntity } from '../../types/team/team';
import { staffApi } from '../../services/manager/staff';
import { shopApi } from '../../services/manager/shop';
import List, { ListRef } from '../../components/Templates/List';
import TeamUpsertModal from './modals/TeamUpsertModal';

const { Text } = Typography;

const statusOptions = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

const Teams: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; record: TeamEntity | null }>({
    open: false,
    mode: 'create',
    record: null
  });

  const [staffs, setStaffs] = useState<any[]>([]);
  const [shops, setShops] = useState<any[]>([]);

  useEffect(() => {
    // Load options
    staffApi.list({ page: 1, pageSize: 1000 }).then(res => {
      if (res.code === 200 && res.data) setStaffs(res.data.items || []);
    });
    shopApi.list({ page: 1, pageSize: 1000 }).then(res => {
      if (res.code === 200 && res.data) setShops(res.data.items || []);
    });
  }, []);

  const fetcher = async (params: any) => {
    const res = await teamApi.list(params);
    return {
      rows: res.data?.items || [],
      pagination: {
        page: params.page,
        page_size: params.pageSize,
        total: res.data?.pagination?.total || 0,
        total_pages: Math.ceil((res.data?.pagination?.total || 0) / params.pageSize)
      }
    };
  };

  const handleDelete = async (record: TeamEntity) => {
    try {
      const res = await teamApi.deleteById(String(record.id));
      if (res.code !== 200) throw new Error(res.message || '删除失败');
      message.success('删除成功');
      listRef.current?.reload();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const columns: ColumnsType<TeamEntity> = [
    { title: '团队名称', dataIndex: 'name', key: 'name' },
    { title: '负责人', key: 'leader', render: (_, r) => r.leader?.name || '-' },
    { title: '状态', dataIndex: 'status', render: (status: number) => <Tag color={status === 1 ? 'success' : 'default'}>{status === 1 ? '启用' : '禁用'}</Tag> },
    { title: '负责店铺', key: 'shops', render: (_, r) => r.shopIds?.map((id: number) => {
      const shop = shops.find(s => s.id === id || Number(s.code) === id);
      return shop ? <Tag key={id}>{shop.name}</Tag> : null;
    }) },
    { title: '团队成员', key: 'staffs', render: (_, r) => r.staffIds?.map((id: number) => {
      const staff = staffs.find(s => s.id === id);
      return staff ? <Tag key={id}>{staff.name}</Tag> : null;
    }) },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => setModalState({ open: true, mode: 'edit', record })}>编辑</Button>
          <Popconfirm title="确定删除？删除后该团队的审核配置也将被同步删除。" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <List<TeamEntity>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>团队配置</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
              新增团队
            </Button>
          </div>
        }
        fetcher={fetcher}
        columns={columns}
        queryFields={[
          { key: 'name', label: '团队名称', type: 'string' },
          { key: 'leaderId', label: '负责人', type: 'select', options: staffs.map(s => ({ label: s.name, value: s.id })) }
        ]}
      />
      <TeamUpsertModal
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        staffs={staffs}
        shops={shops}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
      />
    </>
  );
};

export default Teams;
